import React, { useState } from 'react';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { Tooltip } from '../../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { Icon } from '../../../../../../CommonComponents/OPL/Icon/Icon';
import { MARKET_CONDITIONS, MarketScenarioLabel } from '../../tabs/MonteCarlo/MonteCarlo.meta';
import { ScenarioProjection } from './components/ScenarioProjection/ScenarioProjection';
import { ResultsTable } from './components/ResultsTable/ResultsTable';
import { useMarketScenarios } from './useMarketScenarios';
import { MarketScenariosLoading } from './MarketScenarios.loading';
import { ErrorState } from '../../../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { useSelector } from 'react-redux';
import {
  whatIfsBodySelector,
  whatIfsOrDefaultSelector,
} from '../../../../../../WhatIfs/WhatIfsSelector';
import { MethodologyTooltip } from './components/MethodologyTooltip/MethodologyTooltip';
const DEFAULT_HIDDEN = new Set([MarketScenarioLabel.AboveAverage]);

export const MarketScenarios = () => {
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(() => new Set(DEFAULT_HIDDEN));

  const whatIfsBody = useSelector(whatIfsBodySelector);
  const isWhatIfsOpen = useSelector(whatIfsOrDefaultSelector).open;
  const isWhatIfsActive = isWhatIfsOpen && whatIfsBody != null;

  const { data, isLoading, error, refetch } = useMarketScenarios({ whatIfsBody, isWhatIfsActive });

  const toggleSeries = (name: string) => {
    setHiddenSeries(prev => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  if (isLoading) {
    return <MarketScenariosLoading />;
  }

  if (!!error) {
    return (
      <Box px="16" py="56" mb="24" border="1px solid" borderColor="neutral.200" borderRadius="xl">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  const { scenarioProjection, yearlyResults, milestoneData } = data;

  return (
    <Box>
      <Box display="flex" alignItems="center" mb="24">
        <Text fontSize="body" color="neutral.500" mr="8">
          Projected portfolio outcomes under different market conditions, derived from Monte Carlo
          simulation percentiles.
        </Text>
        <Tooltip content={MethodologyTooltip} placement="bottom" isInteractive>
          <Icon className="far fa-info-circle" color="neutral.500" fontSize="small" />
        </Tooltip>
      </Box>

      <Box display="flex" flexDirection="column" css={({ theme }) => ({ gap: theme.space['24'] })}>
        <ScenarioProjection
          data={scenarioProjection}
          hiddenSeries={hiddenSeries}
          onToggleSeries={toggleSeries}
          milestoneData={milestoneData}
        />

        <ResultsTable
          results={yearlyResults}
          retirementAge={milestoneData?.PrincipalRetirementAge ?? null}
          spouseRetirementAge={milestoneData?.SpouseRetirementAge ?? null}
          hasSpouse={milestoneData?.HasSpouse ?? false}
          principalName={milestoneData?.PrincipalName}
          spouseName={milestoneData?.SpouseName ?? null}
          hiddenSeries={hiddenSeries}
        />
      </Box>
    </Box>
  );
};
